import React, { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { CARD, Label } from "@/components/common";

export const EMP_COLORS = ["#0EA5E9", "#F97316", "#8B5CF6", "#10B981", "#EC4899", "#EAB308", "#14B8A6", "#EF4444", "#6366F1", "#84CC16"];
const pad = (n) => String(n).padStart(2, "0");
const iso = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
const MONTHS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const DOW = ["D", "L", "M", "M", "J", "V", "S"];

export const HrCalendarGrid = ({ emps }) => {
  const [cur, setCur] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const [items, setItems] = useState([]);
  const nDays = new Date(cur.y, cur.m + 1, 0).getDate();
  const days = Array.from({ length: nDays }, (_, i) => i + 1);
  const list = useMemo(() => emps.filter((e) => e.hr_status !== "sorti"), [emps]);

  useEffect(() => {
    api.get(`/hr/calendar?start=${iso(cur.y, cur.m, 1)}&end=${iso(cur.y, cur.m, nDays)}`)
      .then((r) => setItems(r.data.filter((c) => c.kind === "absence" || ["approuve", "demande", "en_attente"].includes(c.status))))
      .catch((e) => toast.error(apiErr(e)));
  }, [cur, nDays]);

  const cellOf = (eid, d) => { const k = iso(cur.y, cur.m, d); return items.find((c) => c.employee_id === eid && c.start <= k && c.end >= k); };
  const countDay = (d) => list.filter((e) => cellOf(e.id, d)).length;
  const shift = (n) => setCur(({ y, m }) => { const t = new Date(y, m + n, 1); return { y: t.getFullYear(), m: t.getMonth() }; });
  const today = new Date(); const isToday = (d) => today.getFullYear() === cur.y && today.getMonth() === cur.m && today.getDate() === d;

  return (
    <div className={`${CARD} p-4 sm:p-6`} data-testid="rh-calendar">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <Label className="flex items-center gap-1.5"><CalendarDays className="h-4 w-4" /> Calendrier des absences</Label>
        <div className="flex items-center gap-2">
          <button data-testid="rh-cal-prev" onClick={() => shift(-1)} className="rounded-full p-1.5 hover:bg-gray-100"><ChevronLeft className="h-4 w-4" /></button>
          <span className="w-36 text-center text-sm font-semibold capitalize" data-testid="rh-cal-month">{MONTHS[cur.m]} {cur.y}</span>
          <button data-testid="rh-cal-next" onClick={() => shift(1)} className="rounded-full p-1.5 hover:bg-gray-100"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-0 text-xs" data-testid="rh-cal-grid">
          <thead><tr>
            <th className="sticky left-0 z-10 min-w-[130px] bg-white p-1 text-left font-semibold text-gray-500">Salarié</th>
            {days.map((d) => { const w = new Date(cur.y, cur.m, d).getDay(); return (
              <th key={d} className={`min-w-[26px] p-0.5 text-center font-medium ${w === 0 ? "text-gray-300" : "text-gray-500"} ${isToday(d) ? "rounded-t-md bg-emerald-50 text-[#065F46]" : ""}`}><div>{DOW[w]}</div><div>{d}</div></th>
            ); })}
          </tr></thead>
          <tbody>
            {list.map((e, i) => { const col = EMP_COLORS[i % EMP_COLORS.length]; return (
              <tr key={e.id} data-testid={`rh-cal-row-${e.id}`}>
                <td className="sticky left-0 z-10 bg-white p-1"><span className="flex items-center gap-1.5 truncate font-semibold"><span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: col }} />{e.name}</span></td>
                {days.map((d) => { const c = cellOf(e.id, d); const sun = new Date(cur.y, cur.m, d).getDay() === 0; const pending = c && c.kind === "conge" && c.status !== "approuve"; return (
                  <td key={d} className={`h-7 border-b border-gray-50 p-0.5 ${sun ? "bg-gray-50" : ""}`}>
                    {c && <div title={`${e.name} · ${c.kind === "conge" ? "Congé" : "Absence"}${pending ? " (en attente)" : ""}`} className="h-5 w-full rounded" style={{ background: col, opacity: pending ? 0.35 : 0.9 }} />}
                  </td>
                ); })}
              </tr>
            ); })}
            <tr>
              <td className="sticky left-0 z-10 bg-white p-1 font-semibold text-gray-500">Absents / jour</td>
              {days.map((d) => { const n = countDay(d); return (
                <td key={d} className="p-0.5 text-center" data-testid={`rh-cal-count-${d}`}>
                  {n > 0 && <span className={`inline-block min-w-[18px] rounded-full px-1 font-bold ${n >= 2 ? "bg-rose-100 text-rose-700" : "bg-gray-100 text-gray-600"}`}>{n}</span>}
                </td>
              ); })}
            </tr>
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-gray-400">Couleur pleine : congé approuvé ou absence · couleur pâle : congé en attente · en rouge : au moins 2 salariés absents le même jour.</p>
    </div>
  );
};
